import React, { useState, useEffect, useContext } from 'react';
import { securePut } from '../config/api';
import { useTranslation } from 'react-i18next';
import { SaveCancel } from './PrimaryButton';
import { updateUser } from '../Shared/UserContext/Actions';
import UserContext from '../Shared/UserContext';
import SnackbarNotification from './SnackbarNotification';
import Input from './Input';

const DynamicForm = ({
  title,
  initialValues = {},
  baseFields = [],
  onSubmit,
  onSuccess,
  onCancel,
  apiUrl,
  module,
}) => {
  const [formValues, setFormValues] = useState({});
  const [, setConditionalBaseFields] = useState([]);
  const { t, i18n } = useTranslation();
  const { dispatch } = useContext(UserContext);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

useEffect(() => {
  const initialFormValues = {};
  const initialConditionalFields = [];

  baseFields.forEach((field) => {
    initialFormValues[field.name] = initialValues[field.name] ?? "";

    if (field.name === "role" && initialValues[field.name] === "ROLE_ZONE_MANAGER") {
      const relatedFields = field.conditionalFields?.ROLE_ZONE_MANAGER || [];
      initialConditionalFields.push(...relatedFields);

      relatedFields.forEach((relatedField) => {
        if (relatedField.name === "repoName") {
          const repoLabel = initialValues.repoName ?? ""; 
          const repoId = initialValues.repoId ?? "";    

          if (Array.isArray(relatedField.options) && relatedField.options.length) {
            const byValue = relatedField.options.find(
              (o) => String(o.value) === String(repoId)
            );
            const byLabel = relatedField.options.find(
              (o) => o.label === repoLabel
            );

            initialFormValues.repoName =
              byValue?.value ??
              byLabel?.value ??
              (repoId || repoLabel || "");
          } else {
            initialFormValues.repoName = repoId || repoLabel || "";
          }
          return;
        }
        initialFormValues[relatedField.name] = initialValues[relatedField.name] ?? "";
      });
    }
    else if (field.name === "role" && initialValues[field.name] === "ROLE_INVENTORY_AGENT") {
      const relatedFields = field.conditionalFields?.ROLE_INVENTORY_AGENT || [];
      initialConditionalFields.push(...relatedFields);

      relatedFields.forEach((relatedField) => {
        if (relatedField.name === "repoName") {
          const repoLabel = initialValues.repoName ?? "";
          const repoId = initialValues.repoId ?? "";

          if (Array.isArray(relatedField.options) && relatedField.options.length) {
            const byValue = relatedField.options.find(
              (o) => String(o.value) === String(repoId)
            );
            const byLabel = relatedField.options.find(
              (o) => o.label === repoLabel
            );

            initialFormValues.repoName =
              byValue?.value ??
              byLabel?.value ??
              (repoId || repoLabel || "");
          } else {
            initialFormValues.repoName = repoId || repoLabel || "";
          }
          return;
        }
        initialFormValues[relatedField.name] = initialValues[relatedField.name] ?? "";
      });
    }
  });

  setFormValues(initialFormValues);
  setConditionalBaseFields(initialConditionalFields);
}, [baseFields, initialValues]);


  const handleInputChange = (name, value) => {
    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      return next;
    });

    const fieldConfig = baseFields.find((field) => field.name === name);
    if (fieldConfig?.conditionalFields) {
      const newFields = fieldConfig.conditionalFields[value] || [];
      setConditionalBaseFields(newFields);

      const newConditionalValues = {};
      newFields.forEach((field) => {
        newConditionalValues[field.name] =
          field.type === "checkbox-dropdown" ? [] : "";
      });

      setFormValues((prev) => ({
        ...prev,
        ...newConditionalValues,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isModifyMode = initialValues && Object.keys(initialValues).length > 0;
    
    const passwordFieldNames = ['password', 'passwordConfirmation', 'password_confirmation', 'oldPassword', 'newPassword'];

    const requiredFields = [...baseFields];
    const missing = requiredFields.filter((f) => {
      if (isModifyMode && passwordFieldNames.includes(f.name)) {
        return false;
      }
      return f.required && !formValues[f.name];
    });
    
    if (missing.length > 0) {
      showSnackbar(
        `Veuillez remplir les champs obligatoires : ${missing.map((f) => f.label).join(', ')}`,
        'error'
      );
      return;
    }

    // Validate password match only if password is provided
    if (formValues.password && (formValues.passwordConfirmation || formValues.password_confirmation)) {
      const confirm = formValues.passwordConfirmation ?? formValues.password_confirmation;
      if (formValues.password !== confirm) {
        showSnackbar(t('placeholder.passwordMismatch'), 'error');
        return;
      }
    }

    const formPayload = { ...formValues };

    delete formPayload.passwordConfirmation;
    delete formPayload.password_confirmation;

    // Replace repoName with repoId if it exists
    if (formPayload.repoName) {
      formPayload.repoId = formPayload.repoName;
      delete formPayload.repoName;
    }

    console.log(formPayload);
    try {
      let response;
        response = await securePut(apiUrl, formPayload, showSnackbar);
      
      onSubmit?.(response?.data);
      onSuccess?.();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const passwordFields = [
    { label: t(`fields.users.oldPassword`), name: "oldPassword", type: "password", isPasswordConfirmation: true },
    { label: t(`fields.users.newPassword`), name: "newPassword", type: "password" },
    { label: t(`fields.users.passwordConfirmation`), name: "password_confirmation", type: "password", isPasswordConfirmation: true },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
      <div className="bg-[#F9FAFC] px-12 py-8 rounded-[12px] shadow-lg w-full max-w-4xl">
        <h2 className="text-[32px] text-[#38485C] font-semibold mb-6">{title}</h2>
        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Base Fields */}
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-6">
              {baseFields
                .filter((field) => field.name !== "footer") // footer handled separately below
                .map((field, index) => (
                  <React.Fragment key={index}>
                    <Input
                      type={field.type || "text"}
                      label={field.label}
                      name={field.name}
                      options={field.options || []}
                      value={formValues[field.name] || ""}
                      required={field.required}
                      onChange={(value) => handleInputChange(field.name, value)}
                      isPasswordConfirmation={field.isPasswordConfirmation}
                    />

                    {field.conditionalFields &&
                      formValues[field.name] in field.conditionalFields &&
                      field.conditionalFields[formValues[field.name]].map(
                        (conditionalField, conditionalIndex) => (
                          <Input
                            key={`conditional-${index}-${conditionalIndex}`}
                            type={conditionalField.type || "text"}
                            label={conditionalField.label}
                            name={conditionalField.name}
                            options={conditionalField.options || []}
                            value={formValues[conditionalField.name] || ""}
                            required={conditionalField.required}
                            onChange={(value) => handleInputChange(conditionalField.name, value)}
                          />
                        )
                      )}
                  </React.Fragment>
                ))}
            </div>
          </div>

          {/* Password Section (only for profile form) */}
          {title === t("form_titles.add_profile") && passwordFields.length > 0 && (
            <div className="space-y-4 mt-6">
              <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
                {t("fields_types.changePass")}
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {passwordFields.map((field, index) => (
                  <Input
                    key={index}
                    type={field.type || "text"}
                    label={field.label}
                    name={field.name}
                    isPasswordConfirmation={field.isPasswordConfirmation}
                    value={formValues[field.name] || ""}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Footer Text Area (Displayed Separately) */}
          {baseFields.some((field) => field.name === "footer") && (
            <div className="space-y-4">
              <div className="col-span-1">
                <label className="text-[16px] font-semibold text-[#ACAFC6] block mb-4">
                  {t(`fields.settings.footer`).toUpperCase()}
                </label>
                <textarea
                  className="w-full h-32 p-3 border border-[#E3E3E3] rounded-lg 
                         focus:border-[#6A79F8] focus:outline-none hover:border-[#6A79F8] text-[16px] font-medium bg-white text-[#000000]}"
                  name="footer"
                  value={formValues.footer || ""}
                  onChange={(e) => handleInputChange("footer", e.target.value)}
                  placeholder={"..."}
                />
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex justify-end space-x-4 mt-6">
            <SaveCancel
              onCancelClick={handleCancel}
              onSaveClick={handleSubmit}
            />
          </div>
        </form>
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default DynamicForm;
